
























































































import { ModuleResponseDTO } from '@/store/modules/jtms/module/module.types';
import { TrainerResponseDTO } from '@/store/modules/jtms/trainer/trainer.types';
import TrainerDropdown from '@/views/AdminGroupSettings/components/GroupLevelAttributes/TrainerDropdown.vue';
import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import ModuleDropdown from './ModuleDropdown.vue';
import PrerequisiteDropdown from './PrerequisiteDropdown.vue';
import {
  CourseModuleComponentItem,
  IndexBooleanPayload,
  UpdateCourseModulePayload
} from '@/store/modules/jtms/course-module/course-module.types';
import { DateOrString } from '@/jbi-shared/types/full-document-revision-object.types';
import DateTimeFieldComponent from './DateTimeFieldComponent.vue';
import { RootState } from '@/store/store';
import { Action, State } from 'vuex-class';

@Component({
  components: {
    ModuleDropdown,
    TrainerDropdown,
    PrerequisiteDropdown,
    DateTimeFieldComponent
  }
})
export default class CourseModuleComponent extends Vue {
  @Prop({ default: false }) isSettings!: boolean;
  @Prop(Object) courseModule!: CourseModuleComponentItem;
  @Prop(Number) index!: number;

  @Action('courseModule/updateCourseModule')
  updateCourseModule!: (payload: UpdateCourseModulePayload) => void;

  @Action('courseModule/updateCourseModulePrerequisites')
  updateCourseModulePrerequisites!: (
    payload: UpdateCourseModulePayload
  ) => void;

  @Action('courseModule/updateCourseModuleFieldError')
  updateCourseModuleFieldError!: (payload: IndexBooleanPayload) => void;

  @State((state: RootState) => state.trainer.trainers)
  trainers!: TrainerResponseDTO[];

  @State(
    (state: RootState) =>
      state.courseModule.courseModulesTabState.activeCourseModules
  )
  activeCourseModules!: CourseModuleComponentItem[];

  @State(
    (state: RootState) =>
      state.courseModule.courseModulesTabState.deletedCourseModules
  )
  deletedCourseModules!: CourseModuleComponentItem[];

  @State((state: RootState) => state.course.courseCreationState.hasModules)
  hasModules!: boolean;

  @State((state: RootState) => state.module.modules)
  modules!: ModuleResponseDTO[];

  isModuleComponentOpened: boolean = true;

  // Local values for v-model binding
  selectedPrerequisites: CourseModuleComponentItem[] = [];
  selectedModule: ModuleResponseDTO | null = null;
  selectedTrainers: TrainerResponseDTO[] = [];
  startDateValue: DateOrString | null = null;
  endDateValue: DateOrString | null = null;
  note: string = '';

  moduleErrorMsg: string = '';
  trainerErrorMsg: string = '';
  endDateErrorMsg: string = '';
  startDateErrorMsg: string = '';

  isInitializing: boolean = true;

  mounted() {
    this.initializeComponentsWithExistingValues();
    this.$nextTick(() => {
      this.isInitializing = false;
    });
  }

  toggle() {
    this.isModuleComponentOpened = !this.isModuleComponentOpened;
  }

  updateErrorState() {
    const hasFieldError = this.fieldErrorMessages().join('').length > 0;
    this.setFieldErrorForCurrentComponent(hasFieldError);
  }

  fieldErrorMessages(): string[] {
    return [
      this.moduleErrorMsg,
      this.trainerErrorMsg,
      this.endDateErrorMsg,
      this.startDateErrorMsg
    ];
  }

  setFieldErrorForCurrentComponent(value: boolean) {
    this.updateCourseModuleFieldError({ index: this.index, data: value });
  }

  initializeComponentsWithExistingValues() {
    if (!this.courseModule) {
      return;
    }

    // Start/End Date
    this.startDateValue = this.courseModule.startDate
      ? new Date(this.courseModule.startDate as string)
      : null;
    this.endDateValue = this.courseModule.endDate
      ? new Date(this.courseModule.endDate as string)
      : null;

    // Note
    this.note = this.courseModule.note ? this.courseModule.note : '';

    // Modules
    if (this.modules && this.courseModule.module) {
      this.selectedModule = this.modules.find(
        (module) => Number(module.id) === this.courseModule.module?.id!
      )!;
    }

    // Trainers
    if (this.trainers && this.courseModule.trainers) {
      this.selectedTrainers = this.trainers.filter((trainer) =>
        this.courseModule.trainers!.some(
          (existingTrainer) => existingTrainer.id === trainer.id
        )
      );
    }

    // Prerequisites
    if (
      // Using index on state variable to prevent syncing issues
      this.activeCourseModules[this.index].prerequisiteIds &&
      this.activeCourseModules[this.index].prerequisiteIds!.length > 0
    ) {
      this.selectedPrerequisites = this.activeCourseModules.filter(
        (courseModules) =>
          this.courseModule.prerequisiteIds!.some(
            (prereq) => prereq.id === courseModules.tempId
          )
      );
    }
  }

  @Watch('trainers')
  @Watch('modules')
  onDataSourceChanged() {
    this.initializeComponentsWithExistingValues();
  }

  @Watch('selectedModule', { deep: true })
  onModuleChange(newModule: ModuleResponseDTO | null) {
    if (this.isInitializing) {
      return; // Do not emit during initialization
    }

    this.updateCourseModule({
      index: this.index,
      data: { module: newModule }
    });
    this.emitUpdateEvent();

    if (this.selectedModule) {
      this.moduleErrorMsg = '';
    } else {
      this.setFieldErrorForCurrentComponent(true);
      this.moduleErrorMsg = 'Module field cannot be empty';
    }
  }

  @Watch('selectedTrainers', { deep: true })
  onTrainerChange(
    newTrainers: TrainerResponseDTO[],
    oldTrainers: TrainerResponseDTO[]
  ) {
    if (this.isInitializing) {
      return; // Do not emit during initialization
    }

    if (oldTrainers === undefined) {
      return;
    }

    const newIds = newTrainers
      .map((t) => t.id)
      .sort()
      .join(',');
    const oldIds = oldTrainers
      .map((t) => t.id)
      .sort()
      .join(',');
    if (newIds === oldIds) {
      return;
    }

    this.updateCourseModule({
      index: this.index,
      data: { trainers: newTrainers }
    });
    this.emitUpdateEvent();

    if (this.selectedTrainers.length > 0) {
      this.trainerErrorMsg = '';
    } else {
      this.setFieldErrorForCurrentComponent(true);
      this.trainerErrorMsg = 'Trainer field cannot be empty';
    }
  }

  @Watch('startDateValue')
  onStartDateChange(newStartDate: Date | null) {
    this.updateCourseModule({
      index: this.index,
      data: { startDate: newStartDate }
    });
    this.emitUpdateEvent();

    if (this.startDateValue) {
      this.startDateErrorMsg = '';
    } else {
      this.setFieldErrorForCurrentComponent(true);
      this.startDateErrorMsg = 'Start date field cannot be empty';
    }
  }

  @Watch('endDateValue')
  onEndDateChange(newEndDate: Date | null) {
    this.updateCourseModule({
      index: this.index,
      data: { endDate: newEndDate }
    });
    this.emitUpdateEvent();

    if (this.endDateValue) {
      this.endDateErrorMsg = '';
    } else {
      this.setFieldErrorForCurrentComponent(true);
      this.endDateErrorMsg = 'End date field cannot be empty';
    }
  }

  @Watch('selectedPrerequisites')
  onPrerequisitesChange(newPrerequisites: CourseModuleComponentItem[]) {
    if (this.isInitializing) {
      return; // Do not emit during initialization
    }

    this.updateCourseModulePrerequisites({
      index: this.index,
      data: {
        prerequisiteIds: newPrerequisites.map((prereq) => {
          return {
            id: prereq.tempId!,
            isExistingCourseModule: prereq.id ? true : false
          };
        })
      }
    });
    this.emitUpdateEvent();
  }

  @Watch('note')
  onNoteChange(newNote: string | '') {
    this.updateCourseModule({
      index: this.index,
      data: { note: newNote }
    });
    this.emitUpdateEvent();
  }

  @Watch('selectedModule', { deep: true })
  @Watch('selectedTrainers', { deep: true })
  @Watch('startDateValue')
  @Watch('endDateValue')
  checkFieldError(): void {
    if (this.isInitializing) {
      return; // Do not emit during initialization
    }
    this.updateErrorState();
  }

  @Emit('delete-module')
  removeCourseModule() {
    return this.index;
  }

  @Emit('update-module')
  emitUpdateEvent() {
    return this.index;
  }
}
